(() => {
  const number = (form, name) => Number.parseFloat(form.elements[name]?.value);
  const whole = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
  const decimal = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

  const emit = (name, tool) => {
    document.dispatchEvent(new CustomEvent(name, { detail: { tool } }));
  };

  document.querySelectorAll('[data-ogh-power-planner]').forEach((root) => {
    const form = root.querySelector('form');
    const results = root.querySelector('[data-ogh-power-results]');
    const error = root.querySelector('[data-ogh-tool-error]');
    const productSelect = root.querySelector('[data-ogh-planning-product]');
    const productContext = root.querySelector('[data-ogh-product-context]');
    const equipmentChecks = root.querySelector('[data-ogh-equipment-checks]');
    if (!form || !results || !error) return;

    productSelect?.addEventListener('change', () => {
      const option = productSelect.selectedOptions[0];
      if (!option?.value) {
        productContext.textContent = 'Choosing a product shows the circuit information from its manual. It will not fill in average watts because real use changes with the building and conditions.';
        return;
      }
      const details = [
        option.dataset.capacity ? `${whole.format(Number(option.dataset.capacity))} BTU` : '',
        option.dataset.voltage ? `${option.dataset.voltage}V` : '',
        option.dataset.mca ? `${option.dataset.mca} A minimum circuit rating` : '',
        option.dataset.mocp ? `${option.dataset.mocp} A largest allowed breaker or fuse` : ''
      ].filter(Boolean);
      productContext.textContent = `${details.join(' · ')}. Use the system's manual and local electrical requirements before installation.`;
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      error.textContent = '';

      if (!form.reportValidity()) return;

      const watts = number(form, 'rated_watts');
      const runHours = number(form, 'run_hours');
      const duty = number(form, 'duty_cycle') / 100;
      const inverterEfficiency = number(form, 'inverter_efficiency') / 100;
      const usableBattery = number(form, 'usable_battery') / 100;
      const systemVoltage = number(form, 'system_voltage');
      const autonomyDays = number(form, 'autonomy_days');
      const solarHours = number(form, 'solar_hours');
      const solarLosses = number(form, 'solar_losses') / 100;
      const existingBatteryWh = number(form, 'existing_battery_wh');
      const panelWatts = number(form, 'panel_watts');
      const inverterWatts = number(form, 'inverter_watts');
      const generatorWatts = number(form, 'generator_watts');

      const values = [watts, runHours, duty, inverterEfficiency, usableBattery, systemVoltage, autonomyDays, solarHours, solarLosses];
      if (values.some((value) => !Number.isFinite(value)) || inverterEfficiency <= 0 || usableBattery <= 0 || systemVoltage <= 0 || solarHours <= 0 || solarLosses >= 1) {
      error.textContent = 'A few numbers need another look. Efficiency, usable battery, battery voltage, and sun hours must be above zero, and solar losses must stay below 100%.';
        return;
      }

      const acDailyWh = watts * runHours * duty;
      const dcDailyWh = acDailyWh / inverterEfficiency;
      const nominalBatteryWh = (dcDailyWh * autonomyDays) / usableBattery;
      const nominalBatteryAh = nominalBatteryWh / systemVoltage;
      const solarArrayW = dcDailyWh / (solarHours * (1 - solarLosses));

      root.querySelector('[data-result="ac_daily_wh"]').textContent = `${whole.format(acDailyWh)} Wh/day`;
      root.querySelector('[data-result="dc_daily_wh"]').textContent = `${whole.format(dcDailyWh)} Wh/day`;
      root.querySelector('[data-result="battery_wh"]').textContent = `${whole.format(nominalBatteryWh)} Wh`;
      root.querySelector('[data-result="battery_ah"]').textContent = `${decimal.format(nominalBatteryAh)} Ah`;
      root.querySelector('[data-result="solar_w"]').textContent = `${whole.format(solarArrayW)} W`;

      const runtimeResult = root.querySelector('[data-optional-result="runtime"]');
      if (Number.isFinite(existingBatteryWh) && existingBatteryWh > 0) {
        const runtimeHours = (existingBatteryWh * usableBattery * inverterEfficiency) / (watts * duty);
        root.querySelector('[data-result="runtime_hours"]').textContent = `${decimal.format(runtimeHours)} hours`;
        runtimeResult.hidden = false;
      } else {
        runtimeResult.hidden = true;
      }

      const panelResult = root.querySelector('[data-optional-result="panels"]');
      if (Number.isFinite(panelWatts) && panelWatts > 0) {
        const panelCount = Math.ceil(solarArrayW / panelWatts);
        root.querySelector('[data-result="panel_count"]').textContent = `${whole.format(panelCount)} ${panelCount === 1 ? 'panel' : 'panels'}`;
        panelResult.hidden = false;
      } else {
        panelResult.hidden = true;
      }

      if (equipmentChecks) {
        const checks = [];
        if (Number.isFinite(inverterWatts) && inverterWatts > 0) {
          checks.push(inverterWatts >= watts
            ? `Your inverter rating is above the average watts you tested. You still need to check startup demand, power quality, other loads, and some extra room for difficult conditions.`
            : `The average watts you tested are higher than your inverter’s continuous rating. Revisit the plan before relying on it.`);
        }
        if (Number.isFinite(generatorWatts) && generatorWatts > 0) {
          checks.push(generatorWatts >= watts
            ? `Your generator rating is above the average watts you tested. You still need to check startup demand, power quality, elevation, other loads, and some extra room for difficult conditions.`
            : `The average watts you tested are higher than your generator’s continuous rating. This plan needs a different assumption or power source.`);
        }
        equipmentChecks.replaceChildren(...checks.map((message) => {
          const paragraph = document.createElement('p');
          paragraph.textContent = message;
          return paragraph;
        }));
      }
      results.hidden = false;
      results.focus({ preventScroll: true });
      results.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'nearest' });
      emit('ogh_power_plan_calculated', 'power_planner');
    });

    form.addEventListener('reset', () => {
      results.hidden = true;
      error.textContent = '';
      equipmentChecks?.replaceChildren();
    });
  });

  document.querySelectorAll('[data-ogh-product-finder]').forEach((root) => {
    const form = root.querySelector('form');
    const products = [...root.querySelectorAll('[data-ogh-finder-product]')];
    const summary = root.querySelector('[data-ogh-finder-summary]');
    const resultRegion = root.querySelector('[data-ogh-finder-results]');
    if (!form || !summary || !resultRegion || products.length === 0) return;

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!form.reportValidity()) return;

      const application = form.elements.application.value;
      const circuit = form.elements.circuit.value;
      let count = 0;

      products.forEach((product) => {
        const applications = (product.dataset.applications || '').split(',').filter(Boolean);
        const voltage = Number.parseInt(product.dataset.voltage, 10);
        const applicationMatch = !application || applications.includes(application);
        const circuitMatch = circuit === 'available' && (voltage === 115 || voltage === 120);
        const match = applicationMatch && circuitMatch;
        product.hidden = !match;
        if (match) count += 1;
      });

      summary.textContent = count === 0
        ? 'Nothing in this small catalog fits those answers well enough to recommend. Try another answer or tell us about the project.'
        : `${count} ${count === 1 ? 'system is' : 'systems are'} worth a closer look. Have the space's heating and cooling needs calculated before choosing a size.`;
      resultRegion.focus({ preventScroll: true });
      emit('ogh_product_finder_completed', 'product_finder');
    });

    form.addEventListener('reset', () => {
      products.forEach((product) => { product.hidden = false; });
      summary.textContent = 'Answer the questions and we\'ll narrow the list.';
    });
  });
})();
